/**
===================================================
Module: frontend.src.components.CreateSessionModal.tsx
Description: Modal component for creating new redaction sessions with PDF upload
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Uppy from "@uppy/core";
import { CreateSessionRequest } from "../types";
import { useSessionCreationStore } from "../stores/sessionCreationStore";
import "@uppy/core/dist/style.css";
import { generatePdfThumbnail, ThumbnailResult } from '../utils/pdfThumbnail';
import '../styles/CreateSessionModal.css';
// Import pdfConfig first to ensure worker is configured before using pdfjsLib
import pdfjsLib from '../utils/pdfConfig';
import { useAuth } from "../contexts/AuthContext";
import { parsePageRange, PageRangeResult } from '../utils/pageRangeParser';

// Static upload constraints
const ALLOWED_FILE_TYPES = [".pdf", "application/pdf"];
const MAX_NUMBER_OF_FILES = 1;
const DEFAULT_MAX_FILE_SIZE_MB = 500;
const DEFAULT_MAX_FILE_PAGES = 100;

interface CreateSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (
    sessionData: CreateSessionRequest,
    selectedFile: File,
    thumbnailBlob: Blob,
  ) => void;
  isUploading: boolean;
  persistState?: boolean;
  onError?: (message: string) => void;
}

const CreateSessionModal: React.FC<CreateSessionModalProps> = ({
  isOpen,
  onClose,
  onNext,
  isUploading,
  persistState = false,
  onError
}) => {
  const { t } = useTranslation('sessions');
  const { user, refreshUser } = useAuth();

  // Effective limits (User Override > Global Default)
  const maxFileSizeMb = user?.quotas?.max_file_size_mb ?? DEFAULT_MAX_FILE_SIZE_MB;
  const maxFilePages = user?.quotas?.max_file_pages ?? DEFAULT_MAX_FILE_PAGES;

  // Total Quota
  const totalLimit = user?.total_page_limit ?? 0;
  const processed = user?.usage?.total_pages_processed ?? 0;
  const pending = user?.usage?.total_pages_pending ?? 0;
  const remainingPages = Math.max(0, totalLimit - (processed + pending));

  const [newSession, setNewSession] = useState<CreateSessionRequest>({
    name: "",
    description: "",
    filename: "input.pdf",
    file_size_bytes: 0,
    file_page_count: 1,
    redaction_classes: [],
    retention_days: 0,
  });
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [displayFilename, setDisplayFilename] = useState<string>("");
  const [error, setError] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uppy, setUppy] = useState<Uppy | null>(null);
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);

  // Page designation — expanded by default (issue #12)
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(true);
  const [pageSelectionMode, setPageSelectionMode] = useState<'all' | 'custom'>('all');
  const [customPageRange, setCustomPageRange] = useState('');
  const [pageRangeResult, setPageRangeResult] = useState<PageRangeResult>({ pages: [], isValid: true });
  const [documentTotalPages, setDocumentTotalPages] = useState(0);

  const {
    sessionData: storeSessionData,
    pdfFile: storePdfFile,
    setPdfFile,
    setThumbnailBlob: setStoreThumbnailBlob,
    setIsGeneratingThumbnail: setStoreIsGeneratingThumbnail,
    resetState,
  } = useSessionCreationStore();

  useEffect(() => {
    if (isOpen) {
      if (persistState && storeSessionData) {
        setNewSession({
          name: storeSessionData.name,
          description: storeSessionData.description,
          filename: "input.pdf",
          file_size_bytes: 0,
          file_page_count: 1,
          redaction_classes: storeSessionData.redaction_classes || [],
          retention_days: storeSessionData.retention_days,
        });

        clearFileState(false);
      } else {
        resetState();

        setNewSession({
          name: "",
          description: "",
          filename: "input.pdf",
          file_size_bytes: 0,
          file_page_count: 1,
          redaction_classes: [],
          retention_days: 0,
        });
        clearFileState(false);
      }

      refreshUser();

      setError("");
      setIsClosing(false);

      initializeUppy();
    }
  }, [
    isOpen,
    persistState,
    storeSessionData,
    resetState,
    setPdfFile,
    setStoreThumbnailBlob,
    setStoreIsGeneratingThumbnail,
  ]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  // Clear all file-related state
  const clearFileState = (clearSessionFields = true) => {
    setSelectedPdfFile(null);
    setDisplayFilename("");
    setThumbnailBlob(null);
    setIsGeneratingThumbnail(false);

    if (clearSessionFields) {
      setNewSession((prev) => ({
        ...prev,
        filename: "input.pdf",
        file_size_bytes: 0,
        thumbnail_data: undefined,
        thumbnail_content_type: undefined,
      }));
    }

    setPdfFile(null);
    setStoreThumbnailBlob(null);
    setStoreIsGeneratingThumbnail(false);

    setError("");
  };

  // Process PDF file and generate thumbnail
  const processPdfFile = async (file: File, fileSize?: number) => {
    setSelectedPdfFile(file);
    setDisplayFilename(file.name || "input.pdf");
    setPdfFile(file);
    setError("");
    setIsGeneratingThumbnail(true);
    setStoreIsGeneratingThumbnail(true);

    try {
      // Validate file size first
      const fileSizeInMB = (fileSize || file.size) / (1024 * 1024);
      if (fileSizeInMB > maxFileSizeMb) {
        throw new Error(
          t('createModal.error.fileSizeExceeded', {
            size: fileSizeInMB.toFixed(2),
            max: maxFileSizeMb
          })
        );
      }

      // Load PDF to count pages
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageCount = pdf.numPages;

      // Validate page count
      if (pageCount > maxFilePages) {
        throw new Error(
          t('createModal.error.pageCountExceeded', {
            pages: pageCount,
            max: maxFilePages
          })
        );
      }

      // Validate total page limit (including pending)
      if (totalLimit > 0 && pageCount > remainingPages) {
        throw new Error(
          t('createModal.error.totalQuotaExceeded', {
            remaining: remainingPages
          })
        );
      }

      // Generate thumbnail from PDF
      const thumbnailResult = await generatePdfThumbnail(file);

      setThumbnailBlob(thumbnailResult.blob);
      setStoreThumbnailBlob(thumbnailResult.blob);

      setNewSession((prev) => ({
        ...prev,
        // Normalize to NFC — macOS reports filenames decomposed (NFD), which
        // AWS Batch rejects when the resulting S3 key is passed as an override.
        filename: (file.name || "input.pdf").normalize("NFC"),
        file_size_bytes: fileSize || file.size || 0,
        file_page_count: pageCount,
        thumbnail_data: thumbnailResult.dataUrl,
        thumbnail_content_type: thumbnailResult.contentType,
      }));

      // Store total pages for page range validation
      setDocumentTotalPages(pageCount);
      // Reset page selection to default
      setPageSelectionMode('all');
      setCustomPageRange('');
      setPageRangeResult({ pages: [], isValid: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onError?.(errorMessage);
      setError(errorMessage);
      // Clear the file selection on error
      clearFileState(true);
    } finally {
      setIsGeneratingThumbnail(false);
      setStoreIsGeneratingThumbnail(false);
    }
  };

  const initializeUppy = () => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxFileSize: maxFileSizeMb * 1024 * 1024,
        maxNumberOfFiles: MAX_NUMBER_OF_FILES,
        allowedFileTypes: ALLOWED_FILE_TYPES,
      },
      autoProceed: false,
    });

    uppyInstance.on("file-added", handleFileAdded);
    uppyInstance.on("file-removed", handleFileRemoved);

    setUppy(uppyInstance);

    return () => {
      if (uppyInstance) {
        uppyInstance.destroy();
      }
    };
  };

  const handleFileAdded = async (file: any) => {
    const pdfFile = file.data as File;
    await processPdfFile(pdfFile, file.size);
  };

  const handleFileRemoved = () => {
    clearFileState(true);
  };

  const handleInputChange = (
    field: keyof CreateSessionRequest,
    value: string,
  ) => {
    setNewSession((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!newSession.name.trim()) {
      setError(t('createModal.error.nameRequired'));
      return;
    }

    if (!selectedPdfFile) {
      setError(t('createModal.error.fileRequired'));
      return;
    }

    if (!thumbnailBlob) {
      setError(t('createModal.error.thumbnailRequired'));
      return;
    }

    // Build selected_pages based on page selection mode
    const sessionData: CreateSessionRequest = { ...newSession };
    if (pageSelectionMode === 'custom' && pageRangeResult.isValid && pageRangeResult.pages.length > 0) {
      sessionData.selected_pages = pageRangeResult.pages;
      sessionData.file_page_count = pageRangeResult.pages.length;
    } else {
      sessionData.selected_pages = null;
    }

    onNext(sessionData, selectedPdfFile, thumbnailBlob);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setNewSession({
        name: "",
        description: "",
        filename: "input.pdf",
        file_size_bytes: 0,
        file_page_count: 1,
        redaction_classes: [],
        retention_days: 0,
      });

      clearFileState(false);
      setIsClosing(false);
      resetState();

      if (uppy) {
        uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
      }

      onClose();
    }, 300);
  };

  const handleFileSelect = async (file: File) => {
    if (file && file.type === 'application/pdf') {
      await processPdfFile(file, file.size);
    } else {
      setError(t('createModal.error.invalidFile'));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const pdfFile = files.find(
      (file) => file.type === "application/pdf" || file.name.endsWith(".pdf"),
    );

    if (pdfFile && uppy) {
      uppy.addFile({
        name: pdfFile.name,
        type: pdfFile.type,
        data: pdfFile,
        size: pdfFile.size,
      });
    }
  };

  // Fixed removeFile function
  const removeFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    clearFileState(true);

    // Clear uppy files
    if (uppy) {
      uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
    }

    // Clear file input
    const fileInput = document.getElementById('pdf-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleUploadClick = () => {
    const fileInput = document.getElementById('pdf-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  // Text subcategory definitions (money is excluded from this release — issue #22)
  const textSubcategories = [
    { id: "text.name", label: t('createModal.redactionType.textSub.name') },
    { id: "text.email", label: t('createModal.redactionType.textSub.email') },
    { id: "text.phone", label: t('createModal.redactionType.textSub.phone') },
    { id: "text.address", label: t('createModal.redactionType.textSub.address') },
    { id: "text.dob", label: t('createModal.redactionType.textSub.dob') },
  ];

  // Non-text redaction types
  const otherRedactionTypes = [
    { id: "seal", label: t('createModal.redactionType.seal.label'), description: t('createModal.redactionType.seal.description') },
    { id: "image.face", label: t('createModal.redactionType.faces.label'), description: t('createModal.redactionType.faces.description') },
    { id: "image.license_plate", label: t('createModal.redactionType.licensePlates.label'), description: t('createModal.redactionType.licensePlates.description') },
  ];

  // Check if any text subcategory is selected
  const selectedTextSubs = (newSession.redaction_classes || []).filter(id => id.startsWith("text."));
  const isTextExpanded = selectedTextSubs.length > 0 || (newSession.redaction_classes || []).includes("text");
  const allTextSubsSelected = textSubcategories.every(sub => (newSession.redaction_classes || []).includes(sub.id));

  // Toggle the parent "Text" checkbox
  const handleTextParentToggle = (checked: boolean) => {
    setNewSession(prev => {
      const classes = (prev.redaction_classes || []).filter(id => !id.startsWith("text"));
      if (checked) {
        // Add all text subcategories
        return { ...prev, redaction_classes: [...classes, ...textSubcategories.map(s => s.id)] };
      }
      return { ...prev, redaction_classes: classes };
    });
  };

  // Toggle an individual text subcategory
  const handleTextSubToggle = (subId: string, checked: boolean) => {
    setNewSession(prev => {
      let classes = (prev.redaction_classes || []).filter(id => id !== "text" && id !== subId);
      if (checked) {
        classes.push(subId);
      }
      return { ...prev, redaction_classes: classes };
    });
  };

  const handleRedactionTypeChange = (typeId: string, checked: boolean) => {
    setNewSession(prev => ({
      ...prev,
      redaction_classes: checked
        ? [...(prev.redaction_classes || []), typeId]
        : (prev.redaction_classes || []).filter(id => id !== typeId)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className={`modal-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{t('createModal.title')}</h2>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label className="form-label">
              {t('createModal.documentName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={newSession.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder={t('createModal.documentNamePlaceholder')}
              required
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('createModal.description')}</label>
            <textarea
              className="form-textarea"
              value={newSession.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder={t('createModal.descriptionPlaceholder')}
              maxLength={500}
              rows={2}
            />
            <div className="character-count">
              {newSession.description?.length || 0}{t('createModal.characterCount')}
            </div>
          </div>

          {/* Add Redaction Types Section */}
          <div className="form-group">
            <label className="form-label">
              {t('createModal.redactionTypes')} <span className="required">*</span>
            </label>
            <div className="redaction-types-container">
              {/* Text parent with subcategories */}
              <div
                className={`redaction-type-item text-parent ${selectedTextSubs.length > 0 ? 'checked' : ''}`}
                data-type="text"
              >
                <label className="redaction-type-label">
                  <input
                    type="checkbox"
                    checked={allTextSubsSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedTextSubs.length > 0 && !allTextSubsSelected;
                    }}
                    onChange={(e) => handleTextParentToggle(e.target.checked)}
                    className="redaction-type-checkbox"
                  />
                  <div className="redaction-type-content">
                    <div className="redaction-type-name">
                      {t('createModal.redactionType.text.label')}
                    </div>
                    <div className="redaction-type-description">
                      {t('createModal.redactionType.text.description')}
                    </div>
                  </div>
                </label>

                {/* Text subcategories */}
                <div className={`text-subcategories ${isTextExpanded ? 'expanded' : ''}`}>
                  {textSubcategories.map((sub) => (
                    <label key={sub.id} className="text-sub-item">
                      <input
                        type="checkbox"
                        checked={(newSession.redaction_classes || []).includes(sub.id)}
                        onChange={(e) => handleTextSubToggle(sub.id, e.target.checked)}
                        className="text-sub-checkbox"
                      />
                      <span className="text-sub-label">{sub.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Other redaction types — side by side */}
              <div className="redaction-types-other">
                {otherRedactionTypes.map((type) => (
                  <div
                    key={type.id}
                    className={`redaction-type-item ${(newSession.redaction_classes || []).includes(type.id) ? 'checked' : ''}`}
                    data-type={type.id}
                  >
                    <label className="redaction-type-label">
                      <input
                        type="checkbox"
                        checked={(newSession.redaction_classes || []).includes(type.id)}
                        onChange={(e) => handleRedactionTypeChange(type.id, e.target.checked)}
                        className="redaction-type-checkbox"
                      />
                      <div className="redaction-type-content">
                        <div className="redaction-type-name">
                          {type.label}
                        </div>
                        <div className="redaction-type-description">
                          {type.description}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Retention Period Section */}
          <div className="form-group">
            <label className="form-label">
              {t('createModal.retentionPeriod')} <span className="required">*</span>
            </label>
            <div className="retention-options">
              {[
                { value: 0, label: t('createModal.retentionImmediate', { days: user?.document_retention_days ?? 30 }) },
                { value: 1, label: t('createModal.retention1Day') },
                { value: 7, label: t('createModal.retention1Week') },
              ].map((option) => (
                <label key={String(option.value)} className={`retention-option ${newSession.retention_days === option.value ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="retention_days"
                    value={String(option.value)}
                    checked={newSession.retention_days === option.value}
                    onChange={() => setNewSession(prev => ({ ...prev, retention_days: option.value }))}
                    className="retention-radio"
                  />
                  <span className="retention-label">{option.label}</span>
                </label>
              ))}
            </div>
            {newSession.retention_days === 0 && (
              <p className="retention-warning">
                ⚠️ {t('createModal.deleteAfterDownloadWarning', { defaultValue: 'This file will be permanently deleted after both versions (Black and Transparent) are downloaded.' })}
              </p>
            )}
          </div>

          {/* Advanced Settings — Page Designation */}
          {selectedPdfFile && documentTotalPages > 0 && (
            <div className="form-group advanced-settings-section">
              <button
                type="button"
                className={`advanced-settings-toggle ${advancedSettingsOpen ? 'open' : ''}`}
                onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
              >
                <svg className="advanced-settings-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>{t('createModal.advancedSettings.title')}</span>
              </button>

              {advancedSettingsOpen && (
                <div className="advanced-settings-content">
                  <div className="page-selection-options">
                    <label className={`page-selection-option ${pageSelectionMode === 'all' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="page_selection"
                        value="all"
                        checked={pageSelectionMode === 'all'}
                        onChange={() => {
                          setPageSelectionMode('all');
                          setPageRangeResult({ pages: [], isValid: true });
                        }}
                        className="page-selection-radio"
                      />
                      <span>{t('createModal.advancedSettings.allPages', { count: documentTotalPages })}</span>
                    </label>

                    <label className={`page-selection-option ${pageSelectionMode === 'custom' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="page_selection"
                        value="custom"
                        checked={pageSelectionMode === 'custom'}
                        onChange={() => setPageSelectionMode('custom')}
                        className="page-selection-radio"
                      />
                      <span>{t('createModal.advancedSettings.customPages')}</span>
                    </label>
                  </div>

                  {pageSelectionMode === 'custom' && (
                    <div className="custom-page-range">
                      <input
                        type="text"
                        className={`form-input page-range-input ${customPageRange && !pageRangeResult.isValid ? 'error' : ''}`}
                        value={customPageRange}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCustomPageRange(val);
                          if (val.trim()) {
                            setPageRangeResult(parsePageRange(val, documentTotalPages));
                          } else {
                            setPageRangeResult({ pages: [], isValid: true });
                          }
                        }}
                        placeholder={t('createModal.advancedSettings.customPlaceholder')}
                      />
                      <div className="page-range-feedback">
                        {customPageRange && !pageRangeResult.isValid && pageRangeResult.error && (
                          <span className="page-range-error">
                            {t(`createModal.advancedSettings.${pageRangeResult.error}`, { total: documentTotalPages })}
                          </span>
                        )}
                        {customPageRange && pageRangeResult.isValid && pageRangeResult.pages.length > 0 && (
                          <span className="page-range-count">
                            {t('createModal.advancedSettings.pagesSelected', { count: pageRangeResult.pages.length })}
                            {totalLimit > 0 && (
                              <> · {t('createModal.remainingPages', { count: Math.max(0, remainingPages - pageRangeResult.pages.length + (newSession.file_page_count || 0)) })}</>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              {t('createModal.pdfFile', { maxSize: maxFileSizeMb, maxPages: maxFilePages })}{" "}
              <span className="required">*</span>
            </label>
            <div className="custom-upload-area">
              {/* Hidden file input */}
              <input
                id="pdf-file-input"
                type="file"
                accept=".pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleFileSelect(file);
                  }
                }}
                className="pdf-file-input-hidden"
              />

              {!selectedPdfFile ? (
                <div
                  className={`upload-zone ${isDragOver ? "drag-over" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}  // Add click handler
                >
                  <div className="upload-icon">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 2V8H20"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="upload-text">
                    <span className="upload-title">{t('createModal.dropFile')}</span>
                    <span className="upload-subtitle">{t('createModal.clickToBrowse')}</span>
                    <div className="upload-file-info">
                      {t('createModal.fileInfo', { maxSize: maxFileSizeMb, maxPages: maxFilePages })}
                    </div>
                    {totalLimit > 0 && (
                      <div className="remaining-quota-info" style={{
                        marginTop: '4px',
                        fontSize: '11px',
                        color: remainingPages < 50 ? 'var(--error-color)' : 'var(--text-secondary)',
                        fontWeight: 500
                      }}>
                        {t('createModal.remainingPages', { count: remainingPages })}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="file-selected">
                  <div className="file-info">
                    <div className="file-icon">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 2V8H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="file-details">
                      <div className="file-name">{displayFilename}</div>
                      <div className="file-size">
                        {(
                          (selectedPdfFile.size || 0) /
                          1024 /
                          1024
                        ).toFixed(2)}{" "}
                        {t('createModal.fileSizeUnit')}
                      </div>
                    </div>
                    <button
                      className="remove-file"
                      onClick={removeFile}
                      type="button"
                      title={t('createModal.removeFile')}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M18 6L6 18M6 6L18 18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <div className="upload-warning-text">
                {t('createModal.uploadWarning')}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleClose}
          >
            {t('createModal.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={
              !selectedPdfFile ||
              !newSession.name.trim() ||
              isUploading ||
              !thumbnailBlob ||
              !newSession.redaction_classes?.length ||
              (pageSelectionMode === 'custom' && (!pageRangeResult.isValid || pageRangeResult.pages.length === 0))
            }
          >
            {isUploading ? t('createModal.uploading') : t('createModal.createDocument')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSessionModal;
